import { type FC } from 'react';

import { CalculateTimepointCell } from '@/components/Samples/SamplePlanTable/CalculateTimepointCell';
import Table from '@/components/UI/Table';
import { mergeColumnVisibility } from '@/components/UI/Table/Reusable/APITable/APITable.utils';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { _isEmpty, _isNil, _isNotBlank } from '@/littledash';
import type { UserTableViewTableName } from '@/model/UserTableView.model';
import { DateUtils } from '@/utils/Date.utils';
import { useUserTableViews } from '@/utils/userTableSettings/UserTableSettingsContext';

import type { SuccessfulOverviewSection } from '../../OverviewAccordion.model';
import OverviewGroupChips from '../OverviewGroupChips';
import { emptyValue, formatMetadataValue } from '../OverviewTreatmentsTable/OverviewTreatmentsTable';
import overviewTableStyles from '../OverviewSectionTable.module.scss';
import styles from './SamplingPlanSectionContent.module.scss';

interface SamplingPlanSectionContentProps {
  section: SuccessfulOverviewSection<'sampling_plan'>;
}

type SamplingPlan = SuccessfulOverviewSection<'sampling_plan'>['rows'][number];

const SAMPLE_PLAN_TABLE: UserTableViewTableName = 'samplePlanV2';

const formatTimepoint = (timepoint: string): string =>
  DateUtils.renderDuration(timepoint, { format: 'short', zeros: true, defaultResponse: timepoint });

const defaultColumnVisibility = (columns: Array<TanStackColumnDef<SamplingPlan>>): Record<string, boolean> =>
  columns.reduce<Record<string, boolean>>((acc, column) => {
    if (column.isVisible === false) {
      acc[column.id as string] = false;
    }
    return acc;
  }, {});

/**
 * Compact sampling-plan rows. Column ids match the sample-plan table where they share a field, and
 * the user's active sample-plan view hides those columns. The server returns only the metadata
 * columns that view turns on, keyed by glossary api id like the sample-plan table.
 */
const SamplingPlanSectionContent: FC<SamplingPlanSectionContentProps> = ({ section }) => {
  const { getActiveSettings } = useUserTableViews();
  const activeColumnVisibility = getActiveSettings(SAMPLE_PLAN_TABLE)?.column_visibility;
  const rows = section.rows;
  const metadataColumns: Array<TanStackColumnDef<SamplingPlan>> = (rows[0]?.metadata ?? []).map(
    ({ glossary_api_id: glossaryApiId, title }) => ({
      id: `meta_${glossaryApiId}`,
      header: title,
      cell: ({ row: { original } }) => {
        const field = original.metadata.find((metadataField) => metadataField.glossary_api_id === glossaryApiId);
        return _isNil(field) ? emptyValue : formatMetadataValue(field.value, field.field_type);
      },
    })
  );
  const columns: Array<TanStackColumnDef<SamplingPlan>> = [
    {
      id: 'name',
      header: 'Name',
      accessorKey: 'name',
    },
    {
      id: 'group',
      header: 'Group',
      accessorKey: 'target',
      enableSorting: false,
      cell: ({
        row: {
          original: { target },
        },
      }) => <OverviewGroupChips groupApiIds={target.group_api_ids} revealGroupsOnClick />,
    },
    {
      id: 'sample_type',
      header: 'Sample type',
      accessorKey: 'sample_type_name',
      cell: ({
        row: {
          original: { sample_type_name: sampleTypeName },
        },
      }) => (_isNotBlank(sampleTypeName) ? sampleTypeName : emptyValue),
    },
    {
      id: 'timepoint',
      header: 'Timepoints',
      accessorKey: 'timepoints',
      cell: ({
        row: {
          original: { timepoints },
        },
      }) =>
        _isEmpty(timepoints) ? (
          emptyValue
        ) : (
          <div className={styles.timepoints} data-test-element="timepoints">
            {timepoints.map((timepoint) => (
              <span
                key={timepoint}
                className={styles.chip}
                data-test-element="timepoint-chip"
                data-test-key={timepoint}
              >
                {formatTimepoint(timepoint)}
              </span>
            ))}
          </div>
        ),
    },
    {
      id: 'calculate-collection-time',
      header: 'Calculate collection time',
      accessorKey: 'calculate_collection_time',
      cell: ({
        row: {
          original: { calculate_collection_time: calculateCollectionTime },
        },
      }) => <CalculateTimepointCell value={calculateCollectionTime} />,
    },
    {
      id: 'volume',
      header: 'Volume',
      accessorKey: 'volume',
      cell: ({
        row: {
          original: { volume },
        },
      }) => (_isNotBlank(volume) ? volume : emptyValue),
    },
    ...metadataColumns,
  ];
  const columnVisibility = mergeColumnVisibility(activeColumnVisibility, defaultColumnVisibility(columns));
  const visibleColumns = columns
    .filter((column) => columnVisibility[column.id as string] !== false)
    .map((column) => ({ ...column, isVisible: true }));

  return (
    <div data-test-component="SamplingPlanSectionContent" data-test-element="container">
      <Table data={rows} columns={visibleColumns} maxHeightClassName={overviewTableStyles.maxHeight} />
    </div>
  );
};

export default SamplingPlanSectionContent;
